import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import MainLayout from "../../../components/layout/MainLayout";
import { useMasterData } from "../../../hooks/useMasterData";
import { usePermissions } from "../../../contexts/PermissionContext";
import { feesApi } from "../api/feesApi";

type SortDir = "asc" | "desc";

const FeeLedgerReportsPage: React.FC = () => {
  const { masters, fetchAcademicSessions, fetchMasters, fetchSections } = useMasterData();
  const { hasAnyPermission } = usePermissions();

  const [tab, setTab] = useState(0);
  const [sessionId, setSessionId] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [sections, setSections] = useState<Array<{ id: string; name: string }>>([]);

  const [search, setSearch] = useState("");
  const [studentId, setStudentId] = useState("");
  const [students, setStudents] = useState<any[]>([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [dayDate, setDayDate] = useState(new Date().toISOString().slice(0, 10));

  const [classPage, setClassPage] = useState(0);
  const [classPageSize, setClassPageSize] = useState(25);
  const [classSortBy, setClassSortBy] = useState("studentName");
  const [classSortDir, setClassSortDir] = useState<SortDir>("asc");

  const [auditPage, setAuditPage] = useState(0);
  const [auditPageSize, setAuditPageSize] = useState(25);
  const [auditActionType, setAuditActionType] = useState("ALL");
  const [auditUserId, setAuditUserId] = useState("");

  const [studentLedger, setStudentLedger] = useState<any>(null);
  const [classSummary, setClassSummary] = useState<any>({ rows: [], totals: {}, pagination: { total: 0 } });
  const [daybook, setDaybook] = useState<any>(null);
  const [audit, setAudit] = useState<any>({ rows: [], pagination: { total: 0 } });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<any>(null);

  const canView = useMemo(
    () => hasAnyPermission(["FEES_LEDGER_VIEW", "FEES_REPORT", "FEES_VIEW", "FEE_VIEW"]),
    [hasAnyPermission],
  );
  const canAudit = useMemo(
    () => hasAnyPermission(["FEES_AUDIT_VIEW", "FEES_REPORT", "FEE_VIEW"]),
    [hasAnyPermission],
  );
  const canExport = useMemo(
    () => hasAnyPermission(["FEES_LEDGER_EXPORT", "FEES_REPORT", "FEE_VIEW"]),
    [hasAnyPermission],
  );

  useEffect(() => {
    fetchAcademicSessions();
    fetchMasters();
  }, [fetchAcademicSessions, fetchMasters]);

  useEffect(() => {
    const active = masters.academicSessions.find((s) => s.isActive);
    if (!sessionId && active) {
      setSessionId(active.id);
    }
  }, [masters.academicSessions, sessionId]);

  useEffect(() => {
    let cancelled = false;
    const loadSections = async () => {
      if (!classId) {
        setSections([]);
        setSectionId("");
        return;
      }
      const rows = await fetchSections(classId);
      if (!cancelled) {
        setSections(Array.isArray(rows) ? rows : []);
      }
    };
    loadSections();
    return () => {
      cancelled = true;
    };
  }, [classId, fetchSections]);

  const searchStudents = async () => {
    if (!sessionId) return;
    try {
      const rows = await feesApi.students({
        sessionId,
        classId: classId || undefined,
        sectionId: sectionId || undefined,
        search: search || undefined,
      });
      setStudents(Array.isArray(rows) ? rows : []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to search students");
    }
  };

  const runStudentLedger = async () => {
    if (!sessionId || !studentId) {
      setError("Session and student are required");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await feesApi.studentLedger(studentId, {
        sessionId,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      });
      setStudentLedger(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to load student ledger");
    } finally {
      setLoading(false);
    }
  };

  const runClassSummary = async () => {
    if (!sessionId || !classId) {
      setError("Session and class are required");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await feesApi.classLedger({
        sessionId,
        classId,
        sectionId: sectionId || undefined,
        search: search || undefined,
        page: classPage + 1,
        pageSize: classPageSize,
        sortBy: classSortBy,
        sortDir: classSortDir,
      });
      setClassSummary(data || { rows: [], totals: {}, pagination: { total: 0 } });
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to load class summary");
    } finally {
      setLoading(false);
    }
  };

  const runDaybook = async () => {
    if (!sessionId || !dayDate) {
      setError("Session and date are required");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await feesApi.ledgerDaybook({ sessionId, date: dayDate });
      setDaybook(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to load daybook");
    } finally {
      setLoading(false);
    }
  };

  const runAudit = async () => {
    if (!sessionId) {
      setError("Session is required");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await feesApi.feeAudit({
        sessionId,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        userId: auditUserId || undefined,
        actionType: auditActionType,
        page: auditPage + 1,
        pageSize: auditPageSize,
      });
      setAudit(data || { rows: [], pagination: { total: 0 } });
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  };

  const refreshActiveTab = async () => {
    if (tab === 0) {
      if (!studentId) return;
      await runStudentLedger();
    }
    if (tab === 1) await runClassSummary();
    if (tab === 2) await runDaybook();
    if (tab === 3) await runAudit();
  };

  const exportReport = async (format: "csv" | "xlsx") => {
    if (!canExport) return;

    try {
      let params: any = { format, sessionId };
      if (tab === 0) {
        if (!studentId) {
          setError("Select a student first");
          return;
        }
        params = {
          ...params,
          type: "student",
          studentId,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
        };
      } else if (tab === 1) {
        if (!classId) {
          setError("Select class first");
          return;
        }
        params = {
          ...params,
          type: "class",
          classId,
          sectionId: sectionId || undefined,
        };
      } else {
        params = {
          ...params,
          type: "daybook",
          date: dayDate,
        };
      }

      const file = await feesApi.getLedgerExportBlob(params);
      const url = URL.createObjectURL(file.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      setMessage("Export generated successfully");
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Export failed");
    }
  };

  const openDetail = (row: any) => {
    setDetailRow(row);
    setDetailOpen(true);
  };

  const toggleClassSort = (field: string) => {
    if (classSortBy === field) {
      setClassSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setClassSortBy(field);
      setClassSortDir("asc");
    }
  };

  useEffect(() => {
    if (!sessionId || !canView) return;
    if (tab === 0 && !studentId) {
      setError(null);
      return;
    }
    refreshActiveTab();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, tab, studentId]);

  useEffect(() => {
    if (tab === 1 && sessionId && classId) {
      runClassSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classPage, classPageSize, classSortBy, classSortDir]);

  useEffect(() => {
    if (tab === 3 && sessionId) {
      runAudit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditPage, auditPageSize]);

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          Fee Ledger Reports
        </Typography>

        {!canView && (
          <Alert severity="error" sx={{ mb: 2 }}>
            You do not have permission to view ledger reports.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>
            {message}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Academic Session"
                fullWidth
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
              >
                {masters.academicSessions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Class"
                fullWidth
                value={classId}
                onChange={(e) => {
                  setClassId(e.target.value);
                  setClassPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {masters.classes.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Section"
                fullWidth
                disabled={!classId}
                value={sectionId}
                onChange={(e) => {
                  setSectionId(e.target.value);
                  setClassPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {sections.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Search (name/scholar)"
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                type="date"
                label="From Date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                type="date"
                label="To Date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                type="date"
                label="Daybook Date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dayDate}
                onChange={(e) => setDayDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                <Button variant="outlined" onClick={searchStudents}>Search Students</Button>
                <TextField
                  select
                  size="small"
                  label="Student"
                  value={studentId}
                  onChange={(e) => setStudentId(e.target.value)}
                  sx={{ minWidth: 300 }}
                >
                  {students.map((s) => (
                    <MenuItem key={s.studentId} value={s.studentId}>
                      {`${s.firstName || ""} ${s.lastName || ""}`.trim()} ({s.scholarNumber || "-"})
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Grid>
            <Grid item xs={12} md={12}>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={refreshActiveTab} disabled={loading || !canView}>
                  {loading ? "Loading..." : "Refresh"}
                </Button>
                <Button variant="outlined" onClick={() => exportReport("xlsx")} disabled={!canExport || tab === 3}>
                  Export XLSX
                </Button>
                <Button variant="outlined" onClick={() => exportReport("csv")} disabled={!canExport || tab === 3}>
                  Export CSV
                </Button>
                <Button variant="outlined" onClick={() => window.print()}>
                  Print View
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Student Ledger" />
            <Tab label="Class Summary" />
            <Tab label="Daybook" />
            <Tab label="Audit Trail" disabled={!canAudit} />
          </Tabs>

          {tab === 0 && (
            <>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
                <Typography variant="body2">Total Fee: {Number(studentLedger?.summary?.totalFee || 0).toFixed(2)}</Typography>
                <Typography variant="body2">Total Paid: {Number(studentLedger?.summary?.totalPaid || 0).toFixed(2)}</Typography>
                <Typography variant="body2">Total Discount: {Number(studentLedger?.summary?.totalDiscount || 0).toFixed(2)}</Typography>
                <Typography variant="body2">Total Refund: {Number(studentLedger?.summary?.totalRefund || 0).toFixed(2)}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Final Due: {Number(studentLedger?.summary?.finalDue || 0).toFixed(2)}
                </Typography>
              </Stack>
              <TableContainer sx={{ maxHeight: 460 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell>Debit</TableCell>
                      <TableCell>Credit</TableCell>
                      <TableCell>Balance</TableCell>
                      <TableCell>Remarks</TableCell>
                      <TableCell>Created By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(studentLedger?.rows || []).map((row: any) => (
                      <TableRow key={row.id} hover onClick={() => openDetail(row)} sx={{ cursor: "pointer" }}>
                        <TableCell>{row.date}</TableCell>
                        <TableCell>{row.transactionType}</TableCell>
                        <TableCell>{row.reference || "-"}</TableCell>
                        <TableCell>{Number(row.debit || 0).toFixed(2)}</TableCell>
                        <TableCell>{Number(row.credit || 0).toFixed(2)}</TableCell>
                        <TableCell>{Number(row.balance || 0).toFixed(2)}</TableCell>
                        <TableCell>{row.remarks || "-"}</TableCell>
                        <TableCell>{row.createdBy || "-"}</TableCell>
                      </TableRow>
                    ))}
                    {!studentLedger?.rows?.length && (
                      <TableRow>
                        <TableCell colSpan={8}>No ledger rows found.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {tab === 1 && (
            <>
              <TableContainer sx={{ maxHeight: 460 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={classSortBy === "studentName"}
                          direction={classSortDir}
                          onClick={() => toggleClassSort("studentName")}
                        >
                          Student Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={classSortBy === "scholarNo"}
                          direction={classSortDir}
                          onClick={() => toggleClassSort("scholarNo")}
                        >
                          Scholar No
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Total Fee</TableCell>
                      <TableCell>Paid</TableCell>
                      <TableCell>Discount</TableCell>
                      <TableCell>Refund</TableCell>
                      <TableCell>Due</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(classSummary?.rows || []).map((row: any) => (
                      <TableRow key={row.studentId} hover onClick={() => openDetail(row)} sx={{ cursor: "pointer" }}>
                        <TableCell>{row.studentName}</TableCell>
                        <TableCell>{row.scholarNo}</TableCell>
                        <TableCell>{Number(row.totalFee || 0).toFixed(2)}</TableCell>
                        <TableCell>{Number(row.paid || 0).toFixed(2)}</TableCell>
                        <TableCell>{Number(row.discount || 0).toFixed(2)}</TableCell>
                        <TableCell>{Number(row.refund || 0).toFixed(2)}</TableCell>
                        <TableCell>{Number(row.due || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                    {!classSummary?.rows?.length && (
                      <TableRow>
                        <TableCell colSpan={7}>No class summary rows found.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 1, mb: 1 }}>
                <Typography variant="body2">
                  Totals: Collection {Number(classSummary?.totals?.totalCollection || 0).toFixed(2)} | Discount {Number(classSummary?.totals?.totalDiscount || 0).toFixed(2)} | Refund {Number(classSummary?.totals?.totalRefund || 0).toFixed(2)} | Due {Number(classSummary?.totals?.totalDue || 0).toFixed(2)}
                </Typography>
              </Box>

              <TablePagination
                component="div"
                count={Number(classSummary?.pagination?.total || 0)}
                page={classPage}
                onPageChange={(_, p) => setClassPage(p)}
                rowsPerPage={classPageSize}
                onRowsPerPageChange={(e) => {
                  setClassPageSize(Number(e.target.value));
                  setClassPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}

          {tab === 2 && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2">Opening Balance</Typography>
                  <Typography variant="h6">{Number(daybook?.openingBalance || 0).toFixed(2)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={3}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2">Total Collection</Typography>
                  <Typography variant="h6">{Number(daybook?.totalCollection || 0).toFixed(2)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={3}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2">Refunds</Typography>
                  <Typography variant="h6">{Number(daybook?.totalRefund || 0).toFixed(2)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={3}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2">Closing Balance</Typography>
                  <Typography variant="h6">{Number(daybook?.closingBalance || 0).toFixed(2)}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>CASH</TableCell>
                      <TableCell>UPI</TableCell>
                      <TableCell>CHEQUE</TableCell>
                      <TableCell>ONLINE</TableCell>
                      <TableCell>OTHER</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>{Number(daybook?.modeWise?.CASH || 0).toFixed(2)}</TableCell>
                      <TableCell>{Number(daybook?.modeWise?.UPI || 0).toFixed(2)}</TableCell>
                      <TableCell>{Number(daybook?.modeWise?.CHEQUE || 0).toFixed(2)}</TableCell>
                      <TableCell>{Number(daybook?.modeWise?.ONLINE || 0).toFixed(2)}</TableCell>
                      <TableCell>{Number(daybook?.modeWise?.OTHER || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Grid>
            </Grid>
          )}

          {tab === 3 && canAudit && (
            <>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 2 }}>
                <TextField
                  select
                  size="small"
                  label="Action Type"
                  value={auditActionType}
                  onChange={(e) => {
                    setAuditActionType(e.target.value);
                    setAuditPage(0);
                  }}
                >
                  <MenuItem value="ALL">ALL</MenuItem>
                  <MenuItem value="RECEIPT">RECEIPT</MenuItem>
                  <MenuItem value="DISCOUNT">DISCOUNT</MenuItem>
                  <MenuItem value="REFUND">REFUND</MenuItem>
                  <MenuItem value="CHEQUE">CHEQUE</MenuItem>
                </TextField>
                <TextField
                  size="small"
                  label="User ID"
                  value={auditUserId}
                  onChange={(e) => {
                    setAuditUserId(e.target.value);
                    setAuditPage(0);
                  }}
                  sx={{ minWidth: 260 }}
                />
                <Button variant="outlined" onClick={runAudit}>Apply</Button>
              </Stack>

              <TableContainer sx={{ maxHeight: 460 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Entity</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Old Value</TableCell>
                      <TableCell>New Value</TableCell>
                      <TableCell>Remarks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(audit?.rows || []).map((row: any, idx: number) => (
                      <TableRow key={`${row.timestamp}-${idx}`} hover onClick={() => openDetail(row)} sx={{ cursor: "pointer" }}>
                        <TableCell>{String(row.timestamp || "").replace("T", " ").slice(0, 19)}</TableCell>
                        <TableCell>{row.userName || row.userId || "-"}</TableCell>
                        <TableCell>{row.action}</TableCell>
                        <TableCell>{row.entity} ({row.entityRef || "-"})</TableCell>
                        <TableCell>{row.amount == null ? "-" : Number(row.amount).toFixed(2)}</TableCell>
                        <TableCell>{String(row.oldValue || "-").slice(0, 80)}</TableCell>
                        <TableCell>{String(row.newValue || "-").slice(0, 80)}</TableCell>
                        <TableCell>{row.remarks || "-"}</TableCell>
                      </TableRow>
                    ))}
                    {!audit?.rows?.length && (
                      <TableRow>
                        <TableCell colSpan={8}>No audit rows found.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={Number(audit?.pagination?.total || 0)}
                page={auditPage}
                onPageChange={(_, p) => setAuditPage(p)}
                rowsPerPage={auditPageSize}
                onRowsPerPageChange={(e) => {
                  setAuditPageSize(Number(e.target.value));
                  setAuditPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </Paper>

        <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>Row Details</DialogTitle>
          <DialogContent>
            <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>
              {JSON.stringify(detailRow, null, 2)}
            </pre>
          </DialogContent>
        </Dialog>
      </Box>
    </MainLayout>
  );
};

export default FeeLedgerReportsPage;
